import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { axios } from '@/axios';
import { config } from '@/config';
import {
  Training,
  Evaluation,
  Flag,
  Import,
  Theme,
  DuoToneCourseFilled,
  DuoToneAssessmentFilled,
  DuoToneVacancyFilled,
  TimeFilled,
  InformationFilled,
} from '@teo/components/icons';
import { Button } from '@teo/components';
import { useGet } from '@/query/fetchHooks';

export const Summary = ({ lpContent, authUser, learningpathId }: any) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<any>(null);
  const [learningpath, setLearningpath] = useState<any>(null);

  useGet(`/learningpath/${learningpathId}`, {
    retry: false,
    onSuccess: (res: any) => {
      setLearningpath(res?.data || undefined);
    },
  });

  function convertToTime(number: number) {
    const hours = Math.floor(number / 3600);
    const minutes = Math.round((number / 3600 - hours) * 60);
    return (
      hours +
      `${t('pages.learn_index.hour')} ` +
      minutes +
      `${t('pages.learn_index.minute')} `
    );
  }

  const handleContentNumber = async (content: any[]): Promise<typeof stats> => {
    const localStats: any = {
      courses: 0,
      themes: 0,
      trainings: 0,
      assessments: 0,
      evaluations: 0,
      milestones: 0,
      vacancies: 0,
      imports: 0,
    };

    for (const el of content) {
      switch (el.subType) {
        case 'learningpath': {
          localStats.themes += 1;
          const responseLp = await axios.get(
            `${config.backend}/learningpathnodedetails?learningpathId=${el?.objectId}&organisationId=${authUser?.activeOrganisationId}`
          );
          const childStats: any = await handleContentNumber(responseLp?.data);

          Object.keys(localStats).forEach((key) => {
            localStats[key] += childStats[key];
          });
          break;
        }

        case 'course':
          localStats.courses += 1;
          break;
        case 'leervacature':
          localStats.vacancies += 1;
          break;
        case 'assessment':
          localStats.assessments += 1;
          break;
        case 'training':
          localStats.trainings += 1;
          break;
        case 'evaluation':
          localStats.evaluations += 1;
          break;
        case 'milestone':
          localStats.milestones += 1;
          break;
        case 'import':
          localStats.imports += 1;
          break;
        default:
          break;
      }
    }

    return localStats;
  };

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (lpContent?.length > 0) {
        const newStats = await handleContentNumber(lpContent);
        if (isMounted) {
          setStats(newStats);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [lpContent]);

  if (!stats) return null;

  const CardTempalate = ({ divClass, iconClass, Icon, number, title }: any) => {
    return (
      <div
        className={`flex flex-col rounded-lg border border-grey-02 p-3 ${divClass}`}
      >
        <Icon className={`w-6 ${iconClass}`} />
        <h3 className="mt-3 mb-1 text-base font-semibold text-black">
          {number}
        </h3>
        <p className="text-sm text-grey-07">{title}</p>
      </div>
    );
  };

  const CheckMessage = ({ style = 'red', Icon, title, text, goBtn }: any) => {
    let cardStyle;
    let iconStyle;
    let textStyle;
    if (style === 'red') {
      cardStyle = 'bg-error-01';
      iconStyle = 'text-error-04';
      textStyle = 'text-error-05';
    }
    if (style === 'yellow') {
      cardStyle = 'bg-warning-01';
      iconStyle = 'text-warning-04';
      textStyle = 'text-warning-05';
    }
    return (
      <div
        className={`flex w-full items-center rounded-lg px-6 py-4 ${cardStyle}`}
      >
        <Icon className={`w-6 min-w-6 ${iconStyle}`} />
        <div className="flex flex-col pl-6 pr-4">
          <h3 className={`mb-1 text-sm font-semibold ${textStyle}`}>{title}</h3>
          {text && <p className={`text-sm ${textStyle}`}>{text}</p>}
        </div>
        {goBtn && (
          <Button className="ml-auto" variant="ghost">
            Go to theme
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <div className="mb-6">
        <h3 className="mb-2 text-base">
          {t('pages.learning_path.publish.summary_settings_heading')}
        </h3>
        <p className='"pt-3 text-sm text-grey-07'>
          {t('pages.learning_path.publish.summary_settings_text')}
        </p>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <CardTempalate
          divClass="basis-1/4"
          iconClass="text-secondary-04"
          Icon={Theme}
          number={stats?.themes}
          title={t('pages.learning_path.themes')}
        />
        <CardTempalate
          divClass="basis-1/4"
          iconClass="fill-primary-05 text-white"
          Icon={DuoToneCourseFilled}
          number={stats?.courses}
          title={t('courseTypes.courses')}
        />
        <CardTempalate
          divClass="basis-1/4"
          iconClass="fill-tint-red text-white"
          Icon={DuoToneAssessmentFilled}
          number={stats?.assessments}
          title={t('courseTypes.assessments')}
        />
        <CardTempalate
          divClass="basis-1/4"
          iconClass="text-error-04 fill-error-04"
          Icon={DuoToneVacancyFilled}
          number={stats?.vacancies}
          title={t('courseTypes.vacancies')}
        />
        <CardTempalate
          divClass="basis-1/4"
          iconClass="text-tint-green"
          Icon={Training}
          number={stats?.trainings}
          title={t('pages.learning_path.trainings')}
        />
        <CardTempalate
          divClass="basis-1/4"
          iconClass="text-tint-purple"
          Icon={Evaluation}
          number={stats?.evaluations}
          title={t('pages.learning_path.evaluations')}
        />
        <CardTempalate
          divClass="basis-1/4"
          iconClass="text-tint-blue"
          Icon={Flag}
          number={stats?.milestones}
          title={t('pages.learning_path.milestones')}
        />
        <CardTempalate
          divClass="basis-1/4"
          iconClass="text-tint-aqua"
          Icon={Import}
          number={stats?.imports}
          title={t('pages.learning_path.imports')}
        />
        {/*learningpath?.lpEstimatedTime ? (
          <CardTempalate
            divClass="col-span-2"
            iconClass="text-tint-indigo"
            Icon={TimeFilled}
            number={convertToTime(Math.abs(learningpath?.lpEstimatedTime))}
            title={t('pages.learning_path.publish.total_estimated')}
          />
        ) : (
          ''
        )*/}
      </div>
      <div className="mt-8 flex flex-col gap-3">
        {(stats?.courses < 1 ||
          stats?.assessments < 1 ||
          stats?.trainings < 1 ||
          stats?.evaluations < 1) && (
          <h3 className="text-base">
            {t('pages.learning_path.publish.summary_settings_heading_2')}
          </h3>
        )}
        {stats?.courses < 1 && (
          <CheckMessage
            Icon={InformationFilled}
            title={t('pages.learning_path.publish.message.no_course')}
            goBtn={true}
          />
        )}
        {stats?.assessments < 1 && (
          <CheckMessage
            Icon={InformationFilled}
            title={t('pages.learning_path.publish.message.no_assessment')}
            goBtn={true}
          />
        )}
        {stats?.trainings < 1 && (
          <CheckMessage
            Icon={InformationFilled}
            title={t('pages.learning_path.publish.message.no_training')}
            goBtn={true}
          />
        )}
        {stats?.evaluations < 1 && (
          <CheckMessage
            Icon={InformationFilled}
            title={t('pages.learning_path.publish.message.no_evaluation')}
            goBtn={true}
          />
        )}
      </div>
    </div>
  );
};
